'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

type FetchPage<T, P> = (params: P, page: number) => Promise<T[]>;

export function usePagedFetch<T, P extends object = object>({
  fetchPage,
  initialData,
  initialParams,
  pageSize,
}: {
  fetchPage: FetchPage<T, P>;
  initialData?: T[];
  initialParams?: P;
  pageSize: number;
}) {
  const [data, setDataState] = useState<T[]>(initialData ?? []);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState((initialData?.length ?? 0) >= pageSize);

  const generationRef = useRef(0);
  const pageRef = useRef(0);
  const paramsRef = useRef<P>((initialParams ?? {}) as P);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(hasMore);
  const pageSizeRef = useRef(pageSize);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  const applyPageResult = useCallback((items: T[], page: number, append: boolean) => {
    pageRef.current = page;
    setDataState(previous => (append ? [...previous, ...items] : items));
    setHasMore(items.length >= pageSizeRef.current);
  }, []);

  const reset = useCallback(
    (filterParams: P) => {
      const generation = ++generationRef.current;
      pageRef.current = 0;
      paramsRef.current = filterParams;
      loadingMoreRef.current = false;
      setLoading(true);
      setLoadingMore(false);
      setHasMore(true);

      fetchPage(filterParams, 0)
        .then(items => {
          if (generation !== generationRef.current) return;
          applyPageResult(items, 0, false);
        })
        .finally(() => {
          if (generation === generationRef.current) {
            setLoading(false);
          }
        });
    },
    [applyPageResult, fetchPage],
  );

  const loadMore = useCallback(() => {
    if (!hasMoreRef.current || loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    const generation = generationRef.current;
    const nextPage = pageRef.current + 1;

    fetchPage(paramsRef.current, nextPage)
      .then(items => {
        if (generation !== generationRef.current) return;
        applyPageResult(items, nextPage, true);
      })
      .finally(() => {
        if (generation === generationRef.current) {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        }
      });
  }, [applyPageResult, fetchPage]);

  const setData = useCallback((items: T[], params?: P) => {
    pageRef.current = 0;
    if (params) {
      paramsRef.current = params;
    }
    setDataState(items);
    setHasMore(items.length >= pageSizeRef.current);
  }, []);

  return {
    data,
    hasMore,
    loadMore,
    loading,
    loadingMore,
    reset,
    setData,
  };
}

export function isNearScrollEnd(
  element: HTMLElement,
  {
    thresholdPx = 240,
  }: {
    thresholdPx?: number;
  } = {},
): boolean {
  const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
  const hasOverflow = element.scrollHeight > element.clientHeight + 1;
  const hasScrolledOrClipped = element.scrollTop > 0 || remaining > 1;
  return hasOverflow && hasScrolledOrClipped && remaining <= thresholdPx;
}
